// *
// * ©2018 EdgeVerve Systems Limited, Bangalore, India. All Rights Reserved.
// *
// * The EdgeVerve proprietary software program ("Program"), is protected by copyrights laws, international treaties
// * and other pending or existing intellectual property rights in India, the United States and other countries. The Program
// * may contain / reference third party or open source components, the rights to which continue to remain with the applicable
// * third party licensors or the open source community as the case may be and nothing here transfers the rights to the third party
// * and open source components, except as expressly permitted. Any unauthorized reproduction, storage, transmission in any
// * form or by any means (including without limitation to electronic, mechanical, printing, photocopying, recording or
// * otherwise), or any distribution of this Program, or any portion of it, may result in severe civil and criminal penalties, and will be
// * prosecuted to the maximum extent possible under the law.
import { Component, OnInit, ViewChild, Input, ChangeDetectorRef, OnDestroy, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from "@angular/router";
import { MasterDataService, MasterTableData, MasterTableAction } from '../../services/master-data.service';
import { ToasterService } from '../../../core/services/toaster.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { MdContentService } from '../../services/mdcontent.service';
import { merge ,  Subscription } from 'rxjs';
import { startWith, switchMap, catchError } from 'rxjs/operators';

@Component({
    selector: 'md-data-table',
    templateUrl: './md-data-table.component.html',
    styleUrls: ['./md-data-table.component.scss'],
    standalone: false
})

//use like: <data-table [dataSource]="dataSource" [displayNames]="displayNames" [pageSize]="pageSize"></data-table>
export class MdDataTableComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  @Input() searchValue: String;
  @Input() dataSource;
  @Input() displayNames;
  @Input() sortColumn;
  @Input() sortOrder;
  @Input() pageSize: number;
  @Input() pageNumber: number;
  @Input() checkBoxRequired: boolean = false;
  @Input() rightAligned = [];
  @Input() entity: String;
  @Input() deleteApi: String;
  @Input() maxColumn: number;
  @Input() hasActions: boolean = false;
  @Input() selectedEntity: any;
  @Input('totalRecords') dataSourceSize: number;
  @Input() actionList = ['View', 'Edit', 'Delete'];
  @Input() sortDisableClear = false;
  @Input() hasPaginator: boolean = true;
  @Input() showTotalRecords: boolean = false;

  masterTable: Subscription;
  alignments = {};
  columns;
  // pageSize: number = 10; //default
  pageIndex: number = 0;
  pageSizeOptions = [10, 25, 50];
  displayedColumns;
  displaySelect: boolean = true;
  completeDataSource;
  selection = new SelectionModel<Element>(true, []);
  dialogRef: MatDialogRef<ConfirmDialogComponent>;
  actionColumnName: string;
  confirmTitle: string;
  confirmMessage: string;
  cancelLabel: string;
  totalRecordsLabel: string;
  showSpinner: boolean = false;
  viewLabel: string;
  editLabel: string;
  deleteLabel: string;
  filterLabel: string;
  hasViewAction: boolean = false;
  hasEditAction: boolean = false;
  hasDeleteAction: boolean = false;

  constructor(
    private router: Router,
    private mdService: MasterDataService,
    private _toaster: ToasterService,
    private dialog: MatDialog,
    private _contenService: MdContentService
  ) {
    this.masterTable = this.mdService.masterTableData.subscribe(value => {
      if (value.data.length > 0) {
        //console.log("New Master Data Table Render Request Received");
        this.showSpinner = true;
        this.displayNames = value.header;
        this.dataSource = value.data;
        this.mdTableInit();
        this.mdService.updateMasterTableData({ header: {}, data: [] });
        this.showSpinner = false;
      }
    });
  }

  ngOnInit() {
    this.mdTableInit();
  }

  mdTableInit() {
    if (this.actionList.length > 0) {
      this.actionList.forEach(element => {
        if (element == 'View') {
          this.hasViewAction = true;
        } else if (element == 'Edit') {
          this.hasEditAction = true;
        } else if (element == 'Delete') {
          this.hasDeleteAction = true;
        }
      });
    } else {
      this.hasActions = false;
    }

    if (this._contenService.getTotalRecordsLabel() == '') {
      throw new Error(
        'Total Records Label is required. Set from Content Service'
      );
    } else {
      this.totalRecordsLabel = this._contenService.getTotalRecordsLabel();
    }

    if (this.hasActions) {
      if (this._contenService.getActionColumnName() == '') {
        throw new Error(
          'Action Column Name is required. Set from Content Service'
        );
      } else {
        this.actionColumnName = this._contenService.getActionColumnName();
      }

      if (this.hasViewAction) {
        if (this._contenService.getViewLabel() == '') {
          throw new Error('View Label is required. Set from Content Service');
        } else {
          this.viewLabel = this._contenService.getViewLabel();
        }
      }

      if (this.hasEditAction) {
        if (this._contenService.getEditLabel() == '') {
          throw new Error('Edit Label is required. Set from Content Service');
        } else {
          this.editLabel = this._contenService.getEditLabel();
        }
      }

      if (this.hasDeleteAction) {
        if (this._contenService.getConfirmDialogTitle() == '') {
          throw new Error(
            'Confirmation Dialog Title is required. Set from Content Service'
          );
        } else {
          this.confirmTitle = this._contenService.getConfirmDialogTitle();
        }
      }

      if (this._contenService.getConfirmDialogMessage() == '') {
        throw new Error(
          'Confirmation Dialog Message is required. Set from Content Service'
        );
      } else {
        this.confirmMessage = this._contenService.getConfirmDialogMessage();
      }

      if (this._contenService.getCancelLabel() == '') {
        throw new Error('Cancel Label is required. Set from Content Service');
      } else {
        this.cancelLabel = this._contenService.getCancelLabel();
      }

      if (this._contenService.getDeleteLabel() == '') {
        throw new Error('Delete Label is required. Set from Content Service');
      } else {
        this.deleteLabel = this._contenService.getDeleteLabel();
      }

      if (this._contenService.getFilterLabel() == '') {
        throw new Error('Filter Label is required. Set from Content Service');
      } else {
        this.filterLabel = this._contenService.getFilterLabel();
      }
    }

    this.completeDataSource = new MatTableDataSource<any>(this.dataSource);
    this.columns = this.getTableColumns(this.displayNames);

    if (this.columns.length > 0) {
      if (this.hasActions)
        this.displayedColumns = this.columns.concat(['actionsColumn']);
      else this.displayedColumns = this.columns;
    }
  }

  isSortingDisable(column){
    let selectedEntity = JSON.parse(this.selectedEntity);
    if (selectedEntity && selectedEntity.disabledSortOnColumns){
      if (selectedEntity.disabledSortOnColumns.length > 0 && selectedEntity.disabledSortOnColumns.indexOf(column) !== -1){
        return true;
      }
    }
    return false;
  }
  setAlignments() {
    if (
      Array.isArray(this.displayedColumns) &&
      Array.isArray(this.rightAligned)
    ) {
      this.displayedColumns.forEach((column, index) => {
        if (this.rightAligned.indexOf(column) >= 0) {
          this.alignments[column] = 'right';
        } else {
          this.alignments[column] = 'left';
        }
      });
    }
  }

  ngAfterViewInit() {
    //console.log("Shared Component ngAfterViewInit -----> ",this.sort, this.paginator, this.pageNumber );
    if (this.dataSource != undefined) {
      this.dataSource.sort = this.sort;
      this.dataSource.sort.active = this.sortColumn;
      this.dataSource.sort.direction = this.sortOrder;
      this.dataSource.paginator = this.paginator;
      this.dataSource.paginator.pageIndex = this.pageNumber;
      this.dataSource.paginator.pageSize = this.pageSize;
    }
    // If the user changes the sort order, reset back to the first page.

    // this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          let sortByColumn = '';
          let sortDirection = '';
          if (this.sort.active != undefined) {
            sortByColumn = this.sort.active;
            sortDirection = this.sort.direction;
          }
          var paginationParams = {
            order: sortDirection,
            sortBy: sortByColumn,
            pageNumber: this.paginator.pageIndex,
            pageSize: this.paginator.pageSize
          };
          if (
            JSON.stringify(paginationParams) !=
            JSON.stringify({
              order: this.sortOrder,
              sortBy: this.sortColumn,
              pageNumber: this.pageNumber,
              pageSize: this.pageSize
            })
          ) {
            //console.log("Change Detected in MD Data Table", JSON.stringify(paginationParams));
            this.mdService.triggerPagination(paginationParams);
          }
          return [];
        }),
        catchError(() => {
          return [];
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.masterTable.unsubscribe();
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.completeDataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.completeDataSource.data.forEach(row => this.selection.select(row));
  }
  goToPriceRequestDetail(col, element): void {
    this.router.navigate(['sample/requestDetail', col]);
  }

  setPageSize(event): void {}

  getTableColumns(displayNames): Array<string> {
    var columnNames = Object.keys(displayNames);
    let columnsCount = columnNames.length;
    if (this.maxColumn != undefined) {
      if (columnsCount > this.maxColumn)
        columnNames.splice(this.maxColumn, columnsCount - this.maxColumn);
    }
    return columnNames;
  }

  performAction(action, data) {
    var tableAction = {
      action: action,
      data: data
    };
    this.mdService.triggerTableAction(tableAction);
  }

  openConfirmationDialog(data) {
    data.isSelected = true;
    this.dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: false
    });

    this.dialogRef.componentInstance.confirmTitle = this.confirmTitle;
    this.dialogRef.componentInstance.confirmMessage = this.confirmMessage;
    this.dialogRef.componentInstance.cancelLabel = this.cancelLabel;

    this.dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // this.cancelOrDelete(data);
        let action: MasterTableAction = {
          action: 'delete',
          data: data
        }
        this.mdService.triggerTableAction(action);
      }
      data.isSelected = false;
      this.dialogRef = null;
    });
  }

  cancelOrDelete(data) {
    this.showSpinner = true;
    let url = this.deleteApi + '/' + this.entity + '/delete/' + data.id;
    this.mdService.deleteRecord(url, data).subscribe(
      value => {
        this.showSpinner = false;
        this._toaster.showToaster(value);
        this.performAction('reload', '');
      },
      error => {
        this.showSpinner = false;
        console.log('Error: ', error);
        if (error && error.error && error.error.message)
          this._toaster.showToaster(error.error.message);
      }
    );
  }

  sortData(data: Sort) {
    //console.log("Sort direction",data.direction);
    //console.log("Column name ", data.active);
    if (this.sort.direction == 'asc') this.sort.direction = 'desc';
    else this.sort.direction = 'asc';
    let sortPaginationData = {
      order: this.sort.direction,
      sortBy: data.active,
      pageNumber: this.paginator.pageIndex,
      pageSize: this.paginator.pageSize
    };
    this.mdService.triggerPagination(sortPaginationData);
  }

  applyFilter(filterValue: string) {
    console.log('applyFilter:', filterValue);
    this.completeDataSource.filter = filterValue.trim().toLowerCase();
  }
}


